// Social renderer: one platform-neutral post frame (brand avatar + name, text with a line clamp
// and "see more", then image or link card) with presets per surface. Presets change only layout
// cues (text limit, clamp, media aspect, caption placement); there are no platform logos or
// trademarks, and labels read "LinkedIn-style" etc.
import { useState } from "react";
import { pickMedia, type PreviewDevice, type PreviewModel } from "../../../../../shared/content-preview";
import { initialOf } from "../format";
import { LinkUnfurlCard } from "./LinkUnfurlCard";

export interface SocialPreset {
  id: string;
  label: string;
  /** Characters the surface allows in one post; null when unbounded for a preview. */
  charLimit: number | null;
  /** Lines shown before "see more"; null shows everything. */
  clampLines: number | null;
  /** CSS aspect-ratio of attached media. */
  mediaAspect: string;
  /** Image-first layouts put the media first and the caption under it. */
  captionBelow: boolean;
  /** Image-first surfaces render a placeholder when no image is attached. */
  imageFirst: boolean;
  /** Pin-style: the title is shown under the image. */
  showTitle?: boolean;
}

export const SOCIAL_PRESETS: Record<string, SocialPreset> = {
  linkedin: { id: "linkedin", label: "LinkedIn-style post", charLimit: 3000, clampLines: 3, mediaAspect: "1.91 / 1", captionBelow: false, imageFirst: false },
  x: { id: "x", label: "X-style post", charLimit: 280, clampLines: null, mediaAspect: "16 / 9", captionBelow: false, imageFirst: false },
  bluesky: { id: "bluesky", label: "Bluesky-style post", charLimit: 300, clampLines: null, mediaAspect: "16 / 9", captionBelow: false, imageFirst: false },
  threads: { id: "threads", label: "Threads-style post", charLimit: 500, clampLines: null, mediaAspect: "4 / 5", captionBelow: false, imageFirst: false },
  instagram_square: { id: "instagram_square", label: "Instagram-style square", charLimit: 2200, clampLines: 2, mediaAspect: "1 / 1", captionBelow: true, imageFirst: true },
  instagram_portrait: { id: "instagram_portrait", label: "Instagram-style portrait", charLimit: 2200, clampLines: 2, mediaAspect: "4 / 5", captionBelow: true, imageFirst: true },
  pin: { id: "pin", label: "Pin-style 2:3", charLimit: 500, clampLines: 3, mediaAspect: "2 / 3", captionBelow: true, imageFirst: true, showTitle: true },
  generic: { id: "generic", label: "Social post", charLimit: null, clampLines: 4, mediaAspect: "1.91 / 1", captionBelow: false, imageFirst: false },
};

function PostText({ text, clampLines }: { text: string; clampLines: number | null }) {
  const [expanded, setExpanded] = useState(false);
  const long = clampLines !== null && (text.split("\n").length > clampLines || text.length > clampLines * 90);
  const clamped = long && !expanded;
  return (
    <div>
      <p
        className="whitespace-pre-line text-sm leading-relaxed text-slate-900"
        style={clamped ? { display: "-webkit-box", WebkitLineClamp: clampLines!, WebkitBoxOrient: "vertical", overflow: "hidden" } : undefined}
        data-testid="preview-social-text"
        data-clamped={clamped ? "true" : "false"}
      >
        {text || <span className="text-slate-400">No post text yet.</span>}
      </p>
      {long && (
        <button type="button" onClick={() => setExpanded((value) => !value)} className="mt-1 text-sm font-medium text-slate-500 hover:underline" data-testid="preview-see-more">
          {expanded ? "see less" : "…see more"}
        </button>
      )}
    </div>
  );
}

export function SocialPost({ model, preset, device }: { model: PreviewModel; preset: SocialPreset; device: PreviewDevice }) {
  const image = pickMedia(model, ["image", "hero", "og", "thumbnail"]);
  const length = [...model.text].length;
  const over = preset.charLimit !== null && length > preset.charLimit;
  const identity = model.identity;
  const media = image ? (
    <img src={image.url} alt={image.alt} className="w-full object-cover" style={{ aspectRatio: preset.mediaAspect }} data-testid="preview-social-media" />
  ) : preset.imageFirst ? (
    <div className="flex w-full items-center justify-center bg-slate-100 text-xs text-slate-500" style={{ aspectRatio: preset.mediaAspect }} data-testid="preview-social-media-missing">
      Image goes here
    </div>
  ) : null;
  return (
    <div className={`min-h-full bg-slate-100 ${device === "mobile" ? "p-0" : "p-8"}`} data-testid="preview-social" data-preset={preset.id}>
      <div className={`mx-auto max-w-[560px] overflow-hidden bg-white ${device === "mobile" ? "" : "rounded-xl border border-slate-200 shadow-sm"}`}>
        <p className="border-b border-slate-100 bg-slate-50 px-4 py-1.5 text-[11px] font-medium uppercase tracking-wide text-slate-500">{preset.label} · preview</p>
        <div className="flex items-center gap-3 px-4 pt-3">
          {identity.logo_url ? (
            <img src={identity.logo_url} alt="" className="h-10 w-10 rounded-full object-cover" />
          ) : (
            <span aria-hidden className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-800 text-sm font-semibold text-white">{initialOf(identity.name)}</span>
          )}
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-slate-900" data-testid="preview-social-name">{identity.name}</p>
            <p className="truncate text-xs text-slate-500">{identity.domain ?? "Just now"}</p>
          </div>
        </div>
        {preset.captionBelow ? (
          <>
            <div className="mt-3">{media}</div>
            {preset.showTitle && <p className="px-4 pt-3 text-base font-semibold text-slate-900" data-testid="preview-social-title">{model.title}</p>}
            <div className="px-4 py-3"><PostText text={model.text} clampLines={preset.clampLines} /></div>
          </>
        ) : (
          <>
            <div className="px-4 py-3"><PostText text={model.text} clampLines={preset.clampLines} /></div>
            {media ?? (model.link.url ? <div className="px-4 pb-3"><LinkUnfurlCard link={model.link} compact /></div> : null)}
          </>
        )}
        <div className="flex items-center justify-between border-t border-slate-100 px-4 py-2 text-xs text-slate-500">
          <span aria-hidden>Like · Comment · Share</span>
          {preset.charLimit !== null && (
            <span className={over ? "font-semibold text-red-600" : ""} data-testid="preview-char-count">
              {length}/{preset.charLimit}{over ? " — too long" : ""}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
